import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, TrendingUp, Users, RefreshCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AiUsageTab — admin-only platform overview of AI spend across all users.
 *
 * Reads from /api/admin/ai-usage/overview. Three sections:
 *   1. Top stats bar (total calls, tokens, sellers active)
 *   2. Per-scope breakdown
 *   3. Top 10 sellers by call count + daily mini-bar series
 *
 * Range selector lets the admin look at 7 / 30 / 90 day windows. No write
 * actions — purely analytical.
 */
export default function AiUsageTab({ language }) {
  const isLV = language === 'lv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/ai-usage/overview`, { params: { days: d } });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const downloadCsv = async () => {
    try {
      const resp = await axios.get(`${API}/admin/ai-usage/export.csv`, {
        params: { days }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kolv-ai-usage-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const scopeLabel = {
    parse:   isLV ? 'Meklēšanas frāzes' : 'Search phrases',
    expand:  isLV ? 'Sinonīmi'           : 'Synonyms',
    suggest: isLV ? 'Sludinājuma palīgs' : 'Listing assistant',
  };

  // Pre-compute peak day count for relative bar heights
  const peakDaily = data?.daily?.reduce((m, d) => Math.max(m, d.count), 0) || 0;

  return (
    <div data-testid="admin-ai-usage-tab" className="space-y-6">
      {/* Header + range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#D97736] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-outfit text-xl font-bold text-[#121212]">
              {isLV ? 'AI patēriņš' : 'AI usage'}
            </h2>
            <p className="text-xs text-[#5C5C5C]">
              {isLV ? 'Pēdējās dienas pēc platformas līmeņa' : 'Platform-level totals'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(opt => (
            <button
              key={opt}
              onClick={() => setDays(opt)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                days === opt
                  ? 'bg-[#D97736] text-white border-[#D97736]'
                  : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736]'
              }`}
              data-testid={`admin-ai-range-${opt}d`}
            >
              {opt}d
            </button>
          ))}
          <Button
            type="button" size="sm" variant="outline" onClick={downloadCsv}
            disabled={loading || !data} className="h-8 gap-1.5 text-xs"
            data-testid="admin-ai-export-csv"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </Button>
          <Button
            type="button" size="sm" variant="ghost" onClick={() => load(days)}
            disabled={loading} className="h-8 w-8 p-0"
            data-testid="admin-ai-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading && !data && (
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI
              label={isLV ? 'Kopā izsaukumi' : 'Total calls'}
              value={data.total_calls.toLocaleString('lv-LV')}
              testid="admin-ai-kpi-calls"
            />
            <KPI
              label={isLV ? 'Apr. tokeni' : 'Est. tokens'}
              value={`~${(data.total_tokens_est || 0).toLocaleString('lv-LV')}`}
              testid="admin-ai-kpi-tokens"
            />
            <KPI
              label={isLV ? 'Aktīvi pārdevēji' : 'Active sellers'}
              value={data.top_users.length}
              testid="admin-ai-kpi-sellers"
            />
            <KPI
              label={isLV ? 'Pasl. dienas' : 'Range'}
              value={`${data.days}d`}
              testid="admin-ai-kpi-range"
            />
          </div>

          {/* Per-scope breakdown */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
            <h3 className="font-medium text-[#121212] mb-3">
              {isLV ? 'Sadalījums pa tipiem' : 'Breakdown by scope'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {['parse', 'expand', 'suggest'].map(scope => {
                const cnt = data.by_scope?.[scope] || 0;
                const tokens = data.tokens_est_by_scope?.[scope] || 0;
                const pct = data.total_calls > 0 ? Math.round((cnt / data.total_calls) * 100) : 0;
                return (
                  <div key={scope} className="rounded-xl border border-[#E5E4E0] p-4" data-testid={`admin-ai-scope-${scope}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#121212]">{scopeLabel[scope]}</span>
                      <Badge variant="outline" className="text-[10px]">{pct}%</Badge>
                    </div>
                    <div className="font-outfit text-2xl font-bold text-[#121212]">
                      {cnt.toLocaleString('lv-LV')}
                    </div>
                    <div className="text-xs text-[#5C5C5C] mt-1">
                      ~{tokens.toLocaleString('lv-LV')} {isLV ? 'tokeni' : 'tokens'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Daily volume sparkline */}
          {data.daily.length > 0 && (
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
              <h3 className="font-medium text-[#121212] mb-3">
                {isLV ? 'Dienas apjoms' : 'Daily volume'}
              </h3>
              <div className="flex items-end gap-1 h-32">
                {data.daily.map(d => (
                  <div
                    key={d.date}
                    className="flex-1 min-w-[4px] bg-gradient-to-t from-[#7C3AED] to-[#D97736] rounded-t"
                    style={{ height: peakDaily ? `${Math.max(4, (d.count / peakDaily) * 100)}%` : '4px' }}
                    title={`${d.date}: ${d.count}`}
                    data-testid={`admin-ai-day-${d.date}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-[#5C5C5C] mt-2">
                <span>{data.daily[0]?.date}</span>
                <span>{data.daily[data.daily.length - 1]?.date}</span>
              </div>
            </div>
          )}

          {/* Top sellers table */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E4E0]">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#5C5C5C]" />
                <h3 className="font-medium text-[#121212]">
                  {isLV ? 'Top 10 pārdevēji' : 'Top 10 sellers'}
                </h3>
              </div>
              <span className="text-xs text-[#5C5C5C]">
                {isLV ? `Pēc izsaukumu skaita (${data.days}d)` : `By calls (${data.days}d)`}
              </span>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E4E0] bg-[#FAFAFA]">
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">
                    {isLV ? 'Lietotājs' : 'User'}
                  </th>
                  <th className="text-right px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">
                    {isLV ? 'Izsaukumi' : 'Calls'}
                  </th>
                  <th className="text-right px-5 py-2.5 text-xs font-medium text-[#5C5C5C] uppercase">
                    {isLV ? 'Tokeni' : 'Tokens'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-5 py-6 text-center text-sm text-[#5C5C5C]">
                      {isLV ? 'Šajā periodā neviens lietotājs nav izmantojis AI' : 'No users have used AI in this period'}
                    </td>
                  </tr>
                ) : data.top_users.map((u, i) => (
                  <tr key={u.user_id} className="border-b border-[#E5E4E0] last:border-b-0" data-testid={`admin-ai-top-row-${i}`}>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#D97736] text-white text-xs font-medium flex items-center justify-center">
                          {(u.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#121212]">{u.name}</div>
                          <div className="text-[10px] text-[#5C5C5C]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-right font-medium text-[#121212]">
                      {u.count.toLocaleString('lv-LV')}
                    </td>
                    <td className="px-5 py-2.5 text-right text-sm text-[#5C5C5C]">
                      ~{u.tokens_est.toLocaleString('lv-LV')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4" data-testid={testid}>
      <div className="text-xs text-[#5C5C5C] mb-1">{label}</div>
      <div className="font-outfit text-2xl font-bold text-[#121212]">{value}</div>
    </div>
  );
}
